import {applySnapshot, cast, destroy, flow, getSnapshot, Instance, types} from "mobx-state-tree";
import {Image, ImageDefaultStore} from './models'
import agent from "../agent";
import {GROUP_TYPE_OPEN} from "../screens/HLPrivacySettingScreen";
import {v4} from 'uuid';

const DEFAULT_GROUP_AVATAR = 'https://cdn-sgp.selectdc.net/faf62e78-1916-11e8-909a-0a586460033a.png';

const createDefaultGroupAvatar = () => Image.create({
    ...ImageDefaultStore,
    url: DEFAULT_GROUP_AVATAR,
    path: DEFAULT_GROUP_AVATAR,
    isLocalImage: false,
    isLoaded: false
});

export const isDefaultGroupImage = (img: any) => {
    return img && img.url === DEFAULT_GROUP_AVATAR;
};

const GroupStore =
    types
        .model({
            id: types.string,
            name: types.string,
            groupType: types.string,
            selectId: types.string,
            fetchingDeeplink: types.boolean,
            isCheckingSelectId: types.boolean,
            isSelectIdAvailable: types.boolean,
            isOpen: types.boolean,
            isGroupHistoryOn: types.boolean,
            isMembersCanPostOn: types.boolean,
            avatar: types.maybeNull(Image),
            isOwner: types.boolean,
            shareToPublic: types.boolean,
            allowCrossExposure: types.boolean,
            extraGroupIds: types.array(types.string),
            featureGroupIds: types.array(types.string),
            welcomePostText: types.string,
            bioGroup: types.string,
            welcomePostImages: types.array(Image),
        })
        .views(self => ({
                get getWelcomePostPayload() {
                    return {
                        id: v4(),
                        type: `new:post:${self.welcomePostImages.length ? "image" : "text"}`,
                        special_type: 0,
                        role: 0,
                        group_context_id: self.id,
                        parent: null,
                        users: null,
                        timestamp: Date.now(),
                        data: {
                            body: self.welcomePostText,
                            textBody: self.welcomePostText,
                            expiration: 0,
                            mentionData: [],
                            items: self.welcomePostImages.map(v => v.getImageItem)
                        }
                    }
                },
            })
        )
        .actions(self => {
                let initialState = {};
                return {
                    afterCreate() {
                        initialState = getSnapshot(self);
                    },
                    reset: () => {
                        applySnapshot(self, initialState);
                    },
                    reUploadAll: function () {
                        self.avatar && self.avatar.reupload();
                        for (let i = 0; i < self.welcomePostImages.length; i++) {
                            self.welcomePostImages[i].reupload()
                        }
                    },
                    setExtraGroupIds: function (extraGroupIds: Array<string>) {
                        self.extraGroupIds = cast(extraGroupIds)
                    },
                    setFeatureGroupIds: function (featureGroupIds: Array<string>) {
                        self.featureGroupIds = cast(featureGroupIds)
                    },
                    setWelcomePostText: function (value: string) {
                        self.welcomePostText = value;
                    },
                    setBioGroup: function (value: string) {
                        self.bioGroup = value;
                    },
                    addLocalWelcomePostImage: function (file: File | null) {
                        if (!file) {
                            // prevent some typescript issue
                            return
                        }
                        const image = Image.create({
                            ...ImageDefaultStore,
                            path: URL.createObjectURL(file),
                            isLocalImage: true
                        });
                        image.setFile(file);
                        image.upload();
                        self.welcomePostImages = cast([...self.welcomePostImages, image]);

                    },
                    addUrlWelcomePostImage: function (value: string) {
                        const image = Image.create({...ImageDefaultStore, path: value, isLocalImage: false})
                        image.upload()
                        self.welcomePostImages = cast([...self.welcomePostImages, image]);
                    },
                    removeWelcomePostImage: function (index: number) {
                        const welcomePostImages = [...self.welcomePostImages];
                        if (index > -1) {
                            welcomePostImages.splice(index, 1);
                        }
                        self.welcomePostImages = cast(welcomePostImages);
                    },
                    setId: function (value: string) {
                        self.id = value;
                    },
                    setIsOwner: function (value: boolean) {
                        self.isOwner = value;
                    },
                    setGroupType: function (value: string) {
                        self.groupType = value;
                    },
                    setIsOpen: function (value: boolean) {
                        self.isOpen = value;
                    },
                    setIsGroupHistoryOn: function (value: boolean) {
                        self.isGroupHistoryOn = value;
                    },
                    setAllowCrossExposure: function (value: boolean) {
                        self.allowCrossExposure = value;
                    },
                    setShareToPublic: function (value: boolean) {
                        self.shareToPublic = value;
                    },
                    setIsMembersCanPostOn: function (value: boolean) {
                        self.isMembersCanPostOn = value;
                    },
                    setFetchingDeeplink: function (value: boolean) {
                        self.fetchingDeeplink = value;
                    },
                    setName: function (value: string) {
                        self.name = value;
                    },
                    removeAvatar() {
                        if (self.avatar) {
                            destroy(self.avatar)
                        }
                        self.avatar = createDefaultGroupAvatar();
                    },
                    setURLAvatar: function (value: string, noNeedToUpload: boolean = false) {
                        if (self.avatar) {
                            destroy(self.avatar)
                        }
                        self.avatar = Image.create({...ImageDefaultStore, path: value, isLocalImage: false});
                        if (noNeedToUpload) {
                            self.avatar.url = value;
                        }
                        else {
                            self.avatar.upload();
                        }
                    },
                    setLocalAvatar: function (file: File) {
                        if (self.avatar) {
                            destroy(self.avatar)
                        }
                        self.avatar = Image.create({
                            ...ImageDefaultStore,
                            path: URL.createObjectURL(file),
                            isLocalImage: true
                        });
                        self.avatar.setFile(file);
                        self.avatar.upload();
                    },
                    checkingForSelectId(value: string) {
                        if (self.selectId !== value) {
                            self.selectId = value;
                            if (value) {
                                self.isCheckingSelectId = true;
                            } else {
                                self.isCheckingSelectId = false;
                                self.isSelectIdAvailable = true
                            }
                        }
                    },
                    setSelectIdIfPossible: flow(function* (value: string) {
                        if (value && self.isCheckingSelectId) {
                            const isGroupExist = yield agent.Group.isSelectIdAvailable(value);
                            if (self.selectId === value) {
                                self.isSelectIdAvailable = isGroupExist.available;
                            }
                        }
                        self.isCheckingSelectId = false;
                    })
                }
            }
        )
        .views(self => ({
            get canProceedForCreatingGroup(): boolean {
                if (self.avatar && !self.avatar.isLoaded)
                    return false;
                return this.canProceedForSelectingGroup && !self.isCheckingSelectId && self.isSelectIdAvailable;
            },
            get canProceedForSelectingGroup(): boolean {
                return !!(self.name);
            },
            get imagesAreValid() {
                let welcomePostUploaded =  true
                for (let i = 0; i < self.welcomePostImages.length; i++) {
                    if (!welcomePostUploaded) break;
                    welcomePostUploaded = self.welcomePostImages[i].isUploaded;
                }
                return !!(self.avatar && self.avatar.isUploaded && welcomePostUploaded);
            },
        }));

export interface IGroupStore extends Instance<typeof GroupStore> {
}

export const GroupDefaultStore = {
    id: '',
    name: '',
    selectId: '',
    isOpen: true,
    groupType: GROUP_TYPE_OPEN,
    isGroupHistoryOn: true,
    shareToPublic: true,
    allowCrossExposure: true,
    isMembersCanPostOn: true,
    isCheckingSelectId: false,
    fetchingDeeplink: false,
    isSelectIdAvailable: true,
    isOwner: true,
    avatar: createDefaultGroupAvatar(),
    extraGroupIds: [],
    featureGroupIds: [],
    welcomePostText: "",
    bioGroup: "",
    welcomePostImages: [],
};

export default GroupStore;
